/**
 * GuardView — PWA del guardia de seguridad
 *
 * Rutas: /guard/*
 * Vistas:
 *   - Login
 *   - Dashboard (lista de incidentes + botón SOS)
 *   - PTT (pantalla de radio al recibir llamada o entrar manualmente)
 *   - Reporte de incidente (foto + texto)
 *   - Videollamada de emergencia (WebRTC via LiveKit)
 *   - Detalle de incidente
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Room, RoomEvent, Track,
  createLocalAudioTrack, createLocalVideoTrack,
} from 'livekit-client'
import { useAuthStore } from '../store/authStore'
import { useWebSocket } from '../hooks/useWebSocket'
import { usePushSubscription } from '../hooks/usePushSubscription'

// ─── Constantes ────────────────────────────────────────────────────────────────

const SEV = {
  low:      { bg: '#DCFCE7', color: '#166534', label: 'Bajo',    icon: '🟢' },
  medium:   { bg: '#FEF9C3', color: '#854D0E', label: 'Medio',   icon: '🟡' },
  high:     { bg: '#FEE2E2', color: '#991B1B', label: 'Alto',    icon: '🔴' },
  critical: { bg: '#F3E8FF', color: '#6B21A8', label: 'Crítico', icon: '🟣' },
}
const STATUS_LABELS = {
  open: 'Abierto', acknowledged: 'Visto',
  in_progress: 'En curso', resolved: 'Resuelto', closed: 'Cerrado',
}

// ─── Estilos comunes ───────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 16,
  border: '1px solid var(--border)', borderRadius: 8,
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
  outline: 'none', boxSizing: 'border-box',
}

const btnPrimary = (danger = false) => ({
  width: '100%', padding: '16px', border: 'none', borderRadius: 8,
  background: danger ? 'var(--danger)' : 'var(--accent)',
  color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
})

// ─── Componente: Login ─────────────────────────────────────────────────────────

function GuardLogin({ onLogin }) {
  const [email,   setEmail]   = useState('')
  const [pass,    setPass]    = useState('')
  const [error,   setError]   = useState('')
  const [loading, setLoading] = useState(false)
  const login = useAuthStore(s => s.login)

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try { await login(email, pass); onLogin() }
    catch (err) { setError(err.message || 'Credenciales incorrectas') }
    finally { setLoading(false) }
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-root)', padding: '24px 20px',
    }}>
      <div style={{
        width: '100%', maxWidth: 360,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 16, padding: '32px 24px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, background: 'var(--accent)', borderRadius: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, margin: '0 auto 12px',
          }}>🛡️</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>RetaliQ Guardia</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Acceso para personal de seguridad</p>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Correo</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Contraseña</label>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} required autoComplete="current-password" style={inputStyle} />
          </div>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--danger-light)', color: 'var(--danger-text)', fontSize: 13 }}>{error}</div>
          )}
          <button type="submit" disabled={loading} style={{ ...btnPrimary(), opacity: loading ? 0.7 : 1, marginTop: 4 }}>
            {loading ? 'Ingresando…' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ─── Componente: PTT Hold-to-Talk inline ───────────────────────────────────────
// Walkie-talkie persistente en el home. Connect lazy al primer press, mantiene
// conexión LiveKit mientras dure el mount. Grupo default 'all' = broadcast del
// tenant. Cambio de grupo vía menú desplegable.

function PttHoldButton({ token, siteId }) {
  const roomRef    = useRef(null)
  const trackRef   = useRef(null)
  const [group,    setGroup]    = useState('all')         // 'all' | { kind:'site', id } | { kind:'zone', id, name }
  const [state,    setState]    = useState('idle')         // idle | connecting | ready | transmitting | error
  const [error,    setError]    = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      try { trackRef.current?.stop() } catch {}
      try { roomRef.current?.disconnect() } catch {}
      roomRef.current = null
      trackRef.current = null
    }
  }, [])

  // Cambio de grupo: desconectar para forzar reconexión al próximo press
  useEffect(() => {
    if (roomRef.current) {
      try { roomRef.current.disconnect() } catch {}
      roomRef.current = null
      setState('idle')
    }
  }, [group])

  function tokenUrl() {
    if (group === 'all') return `/api/ptt/token/all`
    if (group.kind === 'site') return `/api/ptt/token/site/${group.id}`
    if (group.kind === 'zone') return `/api/ptt/token/zone/${group.id}`
    return `/api/ptt/token/all`
  }

  async function ensureConnected() {
    if (roomRef.current && (state === 'ready' || state === 'transmitting')) return true
    setError(null); setState('connecting')
    try {
      const r = await fetch(tokenUrl(), { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) throw new Error(`token ${r.status}`)
      const d = await r.json()
      const room = new Room({ adaptiveStream: true })
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach(); el.style.display = 'none'
          document.body.appendChild(el)
        }
      })
      room.on(RoomEvent.TrackUnsubscribed, (track) => { track.detach().forEach(el => el.remove()) })
      room.on(RoomEvent.Disconnected, () => { roomRef.current = null; setState('idle') })
      await room.connect(d.livekit_url, d.token)
      roomRef.current = room
      setState('ready')
      return true
    } catch (e) {
      setError(e.message); setState('error')
      return false
    }
  }

  async function startTransmit() {
    if (state === 'transmitting' || state === 'connecting') return
    const ok = await ensureConnected()
    if (!ok) return
    try {
      trackRef.current = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true })
      await roomRef.current.localParticipant.publishTrack(trackRef.current)
      setState('transmitting')
    } catch {
      setError('Sin acceso al micrófono')
      setState('ready')
    }
  }

  async function stopTransmit() {
    if (state !== 'transmitting') return
    try {
      if (trackRef.current && roomRef.current) {
        await roomRef.current.localParticipant.unpublishTrack(trackRef.current)
        trackRef.current.stop()
        trackRef.current = null
      }
    } catch {}
    setState('ready')
  }

  const active   = state === 'transmitting'
  const busy     = state === 'connecting'
  const groupLbl = group === 'all'
    ? 'Equipo'
    : group.kind === 'site' ? `Sitio ${group.id}`
    : group.name || `Zona ${group.id}`

  return (
    <div style={{ position: 'relative' }}>
      <button
        onPointerDown={e => { e.preventDefault(); startTransmit() }}
        onPointerUp={e => { e.preventDefault(); active && stopTransmit() }}
        onPointerLeave={() => active && stopTransmit()}
        disabled={busy}
        style={{
          width: '100%', padding: '14px 16px', border: 'none', borderRadius: 8,
          background: active ? '#dc2626' : 'var(--accent, #2563EB)',
          color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          userSelect: 'none', transition: 'background 0.15s',
          boxShadow: active ? '0 0 24px rgba(220,38,38,0.5)' : 'none',
        }}
      >
        <span style={{ fontSize: 18 }}>{active ? '🔴' : busy ? '⏳' : '🎙️'}</span>
        <span style={{ flex: 1, textAlign: 'left' }}>
          {active ? 'TRANSMITIENDO — suelta para parar' : busy ? 'Conectando…' : 'Mantén para hablar'}
        </span>
        <span style={{ fontSize: 11, opacity: 0.85 }}>· {groupLbl}</span>
      </button>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button
          type="button"
          onClick={() => setMenuOpen(v => !v)}
          style={{
            padding: '4px 10px', border: '1px solid var(--border)',
            borderRadius: 4, background: 'transparent', color: 'var(--text-muted)',
            fontSize: 11, cursor: 'pointer',
          }}
        >
          Cambiar grupo ▾
        </button>
      </div>

      {menuOpen && (
        <div style={{
          position: 'absolute', bottom: '100%', right: 0,
          background: 'var(--bg-surface, #fff)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 4, marginBottom: 4, zIndex: 10,
          minWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
          <GroupOption label="👥 Equipo (broadcast)" active={group === 'all'}
            onClick={() => { setGroup('all'); setMenuOpen(false) }} />
          {siteId && (
            <GroupOption label={`🏪 Sitio ${siteId}`} active={group.kind === 'site' && group.id === siteId}
              onClick={() => { setGroup({ kind: 'site', id: siteId }); setMenuOpen(false) }} />
          )}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 4, padding: '4px 8px', borderRadius: 4,
          background: 'rgba(239,68,68,0.1)', color: '#dc2626', fontSize: 11,
        }}>{error}</div>
      )}
    </div>
  )
}

function GroupOption({ label, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 12px', cursor: 'pointer', borderRadius: 4,
        background: active ? 'rgba(37,99,235,0.1)' : 'transparent',
        fontSize: 13, color: active ? 'var(--accent, #2563EB)' : 'var(--text-primary)',
        fontWeight: active ? 600 : 400,
      }}
    >{label}</div>
  )
}

// ─── Componente: PTT Screen ────────────────────────────────────────────────────

function PttScreen({ call, token, onClose }) {
  const roomRef  = useRef(null)
  const trackRef = useRef(null)
  const [state,    setState]    = useState('connecting')  // connecting | ready | transmitting | ended
  const [speakers, setSpeakers] = useState([])
  const [error,    setError]    = useState(null)

  useEffect(() => {
    const room = new Room({ adaptiveStream: true })
    roomRef.current = room

    room.on(RoomEvent.ActiveSpeakersChanged, sp => setSpeakers(sp.map(p => p.identity)))
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach()
        el.style.display = 'none'
        document.body.appendChild(el)
      }
    })
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach(el => el.remove())
    })
    room.on(RoomEvent.Disconnected, () => setState('ended'))

    room.connect(call.livekit_url, call.token)
      .then(() => setState('ready'))
      .catch(e => { setError(e.message); setState('ended') })

    return () => {
      if (trackRef.current) {
        room.localParticipant.unpublishTrack(trackRef.current).catch(() => {})
        trackRef.current.stop()
      }
      room.disconnect()
    }
  }, [call.room])

  async function startPTT() {
    if (state !== 'ready') return
    try {
      trackRef.current = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true })
      await roomRef.current.localParticipant.publishTrack(trackRef.current)
      setState('transmitting')
    } catch { setError('No se pudo acceder al micrófono') }
  }

  async function stopPTT() {
    if (state !== 'transmitting') return
    try {
      await roomRef.current.localParticipant.unpublishTrack(trackRef.current)
      trackRef.current.stop()
      trackRef.current = null
    } catch {}
    setState('ready')
  }

  const active = state === 'transmitting'
  const ready  = state === 'ready'

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: active ? '#1a0a0a' : '#0a0f1a',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'space-between',
      padding: '40px 24px 48px',
      transition: 'background 0.3s',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', width: '100%' }}>
        <div style={{ fontSize: 12, color: '#666', letterSpacing: '0.1em', marginBottom: 8 }}>
          LLAMADA ENTRANTE
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
          {call.caller}
        </div>
        {(call.site_name || call.zone_name) && (
          <div style={{ fontSize: 14, color: '#888' }}>
            {call.site_name || call.zone_name}
          </div>
        )}
        {call.message && (
          <div style={{
            marginTop: 12, padding: '10px 16px', borderRadius: 8,
            background: 'rgba(255,255,255,0.08)', color: '#ccc', fontSize: 13,
          }}>{call.message}</div>
        )}
      </div>

      {/* Botón PTT grande */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        {speakers.length > 0 && (
          <div style={{ fontSize: 13, color: '#10b981' }}>
            🔊 {speakers.length > 1 ? 'Central hablando' : 'Central hablando'}
          </div>
        )}

        <button
          onPointerDown={e => { e.preventDefault(); ready && startPTT() }}
          onPointerUp={e => { e.preventDefault(); active && stopPTT() }}
          onPointerLeave={() => active && stopPTT()}
          disabled={state === 'connecting' || state === 'ended'}
          style={{
            width: 160, height: 160, borderRadius: '50%',
            border: `4px solid ${active ? '#ef4444' : '#3b82f6'}`,
            background: active ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.15)',
            color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.15s',
            boxShadow: active ? '0 0 40px rgba(239,68,68,0.5)' : '0 0 20px rgba(59,130,246,0.3)',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: 36 }}>{active ? '🔴' : state === 'connecting' ? '⏳' : '🎙️'}</span>
          <span style={{ fontSize: 12 }}>
            {active ? 'TRANSMITIENDO' : state === 'connecting' ? 'Conectando…' : state === 'ended' ? 'Finalizado' : 'MANTÉN PARA\nHABLAR'}
          </span>
        </button>

        <div style={{ fontSize: 11, color: '#555', textAlign: 'center' }}>
          {active ? 'Suelta para dejar de transmitir' : ready ? 'Mantén presionado para hablar' : ''}
        </div>
      </div>

      {/* Colgar */}
      <button
        onClick={onClose}
        style={{
          padding: '14px 40px', borderRadius: 50,
          background: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444',
          color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}
      >
        📵 Finalizar llamada
      </button>

      {error && (
        <div style={{
          position: 'absolute', bottom: 120, left: 20, right: 20,
          padding: '10px 14px', borderRadius: 8,
          background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: 12,
          textAlign: 'center',
        }}>{error}</div>
      )}
    </div>
  )
}

// ─── Componente: Videollamada de emergencia ────────────────────────────────────

function EmergencyVideoCall({ token, siteId, onClose }) {
  const roomRef     = useRef(null)
  const videoRef    = useRef(null)
  const remoteRef   = useRef(null)
  const [state,     setState]     = useState('connecting')
  const [hasRemote, setHasRemote] = useState(false)

  useEffect(() => {
    if (!siteId) return
    let room

    async function start() {
      // Obtener token para room de videollamada de emergencia
      const res = await fetch(`/api/ptt/token/site/${siteId}?can_publish=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) { setState('error'); return }
      const data = await res.json()

      room = new Room()
      roomRef.current = room

      room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (track.kind === Track.Kind.Video) {
          setHasRemote(true)
          if (remoteRef.current) track.attach(remoteRef.current)
        }
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach()
          el.style.display = 'none'
          document.body.appendChild(el)
        }
      })
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach(el => el.remove())
        if (track.kind === Track.Kind.Video) setHasRemote(false)
      })
      room.on(RoomEvent.Disconnected, () => setState('ended'))

      await room.connect(data.livekit_url, data.token)

      // Publicar video + audio del guardia
      const [audioTrack, videoTrack] = await Promise.all([
        createLocalAudioTrack(),
        createLocalVideoTrack({ facingMode: 'environment' }),  // cámara trasera
      ])
      await room.localParticipant.publishTrack(audioTrack)
      await room.localParticipant.publishTrack(videoTrack)
      if (videoRef.current) videoTrack.attach(videoRef.current)
      setState('connected')
    }

    start().catch(e => { console.error(e); setState('error') })

    return () => {
      if (room) room.disconnect()
    }
  }, [siteId, token])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: '#000', display: 'flex', flexDirection: 'column',
    }}>
      {/* Video remoto (central) */}
      <div style={{ flex: 1, position: 'relative', background: '#111' }}>
        {hasRemote ? (
          <video ref={remoteRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: '#555',
          }}>
            <span style={{ fontSize: 40, marginBottom: 8 }}>📡</span>
            <span style={{ fontSize: 13 }}>
              {state === 'connecting' ? 'Conectando con central…' : state === 'error' ? 'Error de conexión' : 'Esperando respuesta de central…'}
            </span>
          </div>
        )}

        {/* Video local (guardia) — esquina */}
        <div style={{
          position: 'absolute', bottom: 16, right: 16,
          width: 100, height: 140, borderRadius: 10,
          overflow: 'hidden', border: '2px solid #333',
          background: '#000',
        }}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>

      {/* Controles */}
      <div style={{
        padding: '20px 24px 36px',
        background: '#0a0a0a',
        display: 'flex', justifyContent: 'center', gap: 20,
      }}>
        <button
          onClick={onClose}
          style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#ef4444', border: 'none', color: '#fff',
            fontSize: 24, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >📵</button>
      </div>
    </div>
  )
}

// ─── Componente: Reportar incidente ───────────────────────────────────────────

function NewIncidentForm({ token, onCreated, onCancel }) {
  const [title,       setTitle]    = useState('')
  const [description, setDesc]     = useState('')
  const [severity,    setSeverity] = useState('medium')
  const [photo,       setPhoto]    = useState(null)
  const [preview,     setPreview]  = useState(null)
  const [saving,      setSaving]   = useState(false)
  // GPS — se solicita una vez al abrir el form
  const [gps,         setGps]      = useState(null)   // { lat, lon, accuracy }
  const [gpsState,    setGpsState] = useState('pending')  // pending | granted | denied | unavailable

  // Pedir GPS al montar — el guardia ya autorizó la app, este permiso es del browser.
  useEffect(() => {
    if (!('geolocation' in navigator)) { setGpsState('unavailable'); return }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setGps({
          lat: +pos.coords.latitude.toFixed(6),
          lon: +pos.coords.longitude.toFixed(6),
          accuracy: Math.round(pos.coords.accuracy),
        })
        setGpsState('granted')
      },
      err => { setGpsState(err.code === 1 ? 'denied' : 'unavailable') },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    )
  }, [])

  const handlePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => { setPreview(ev.target.result); setPhoto(ev.target.result.split(',')[1]) }
    reader.readAsDataURL(file)
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    try {
      const body = {
        title, description, severity,
        snapshot_b64: photo,
        source: 'guard_report',
      }
      if (gps) {
        body.lat = gps.lat
        body.lon = gps.lon
        body.gps_accuracy = gps.accuracy
      }
      const res = await fetch('/api/incidents/', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) onCreated(await res.json())
    } finally { setSaving(false) }
  }

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Reportar incidente</h2>
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>SEVERIDAD</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {Object.entries(SEV).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setSeverity(k)} style={{
                padding: '10px 8px', borderRadius: 8,
                border: severity === k ? `2px solid ${v.color}` : '1px solid var(--border)',
                background: severity === k ? v.bg : 'var(--bg-surface)',
                color: v.color, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>{v.icon} {v.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>TÍTULO</label>
          <input value={title} onChange={e => setTitle(e.target.value)} required placeholder="Ej: Persona sospechosa en acceso" style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>DESCRIPCIÓN (opcional)</label>
          <textarea value={description} onChange={e => setDesc(e.target.value)} rows={3} placeholder="Detalles adicionales…"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font)' }} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>FOTO DEL INCIDENTE</label>
          {preview ? (
            <div style={{ position: 'relative' }}>
              <img src={preview} alt="preview" style={{ width: '100%', borderRadius: 8, maxHeight: 200, objectFit: 'cover' }} />
              <button type="button" onClick={() => { setPhoto(null); setPreview(null) }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
          ) : (
            <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 20, border: '2px dashed var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
              <span style={{ fontSize: 28 }}>📷</span>
              Tomar foto con la cámara
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                Solo cámara — cadena de custodia preservada
              </span>
              {/* capture=environment fuerza cámara trasera en mobile.
                  Sin 'multiple' y con accept restrictivo. La galería puede
                  aparecer en algunos browsers, pero el flujo natural es cámara. */}
              <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
            </label>
          )}
        </div>

        {/* Badge GPS — se intenta capturar al abrir el form */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderRadius: 6,
          background: gpsState === 'granted' ? 'rgba(16, 185, 129, 0.10)' :
                      gpsState === 'denied'  ? 'rgba(239, 68, 68, 0.10)' :
                                               'rgba(245, 158, 11, 0.10)',
          fontSize: 11, color: 'var(--text-secondary)',
        }}>
          <span style={{ fontSize: 14 }}>
            {gpsState === 'granted' ? '📍' :
             gpsState === 'denied'  ? '🚫' :
             gpsState === 'unavailable' ? '⚠️' : '⏳'}
          </span>
          <div style={{ flex: 1 }}>
            {gpsState === 'granted' && gps && (
              <>
                <strong style={{ color: '#10b981' }}>Ubicación capturada</strong>
                {' · '}
                <span style={{ fontFamily: 'monospace' }}>
                  {gps.lat.toFixed(5)}, {gps.lon.toFixed(5)}
                </span>
                {' '}<span style={{ color: 'var(--text-muted)' }}>±{gps.accuracy}m</span>
              </>
            )}
            {gpsState === 'pending' && 'Solicitando ubicación…'}
            {gpsState === 'denied'  && (
              <span style={{ color: '#dc2626' }}>
                Sin permiso de ubicación. El reporte se envía sin GPS.
              </span>
            )}
            {gpsState === 'unavailable' && 'GPS no disponible en este dispositivo.'}
          </div>
        </div>
        <button type="submit" disabled={saving || !title.trim()} style={{ ...btnPrimary(true), opacity: saving || !title.trim() ? 0.7 : 1, marginTop: 4 }}>
          {saving ? 'Enviando…' : '🚨 Reportar incidente'}
        </button>
      </form>
    </div>
  )
}

// ─── Componente: Detalle incidente ─────────────────────────────────────────────

function IncidentDetail({ inc: initialInc, token, onBack }) {
  const [inc,  setInc]  = useState(initialInc)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const STATUS_NEXT = { open: 'acknowledged', acknowledged: 'in_progress', in_progress: 'resolved', resolved: 'closed' }
  const STATUS_BTN  = { open: 'Marcar como visto', acknowledged: 'Iniciar atención', in_progress: 'Resolver', resolved: 'Cerrar' }

  useWebSocket(msg => {
    if (msg.type === 'incident_updated' && msg.incident?.id === inc.id)
      setInc(p => ({ ...p, ...msg.incident }))
  })

  const updateStatus = async () => {
    const next = STATUS_NEXT[inc.status]
    if (!next) return
    const res = await fetch(`/api/incidents/${inc.id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    })
    if (res.ok) setInc(p => ({ ...p, status: next }))
  }

  const addNote = async () => {
    if (!note.trim()) return
    setSaving(true)
    await fetch(`/api/incidents/${inc.id}/events`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'note', description: note }),
    })
    setNote('')
    setSaving(false)
  }

  const sev = SEV[inc.severity] || SEV.medium

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', padding: '14px 16px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1 }}>Incidente #{inc.id}</h2>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sev.bg, color: sev.color }}>{sev.icon} {sev.label}</span>
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{STATUS_LABELS[inc.status]}</span>
        </div>
      </div>
      <div style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{inc.title}</div>
          {inc.description && <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>{inc.description}</p>}
        </div>
        {inc.snapshot_b64 && (
          <img src={`data:image/jpeg;base64,${inc.snapshot_b64}`} alt="Evidencia" style={{ width: '100%', borderRadius: 10, border: '1px solid var(--border)' }} />
        )}
        {STATUS_NEXT[inc.status] && (
          <button onClick={updateStatus} style={btnPrimary()}>{STATUS_BTN[inc.status]}</button>
        )}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase' }}>Agregar nota</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Escribe una actualización…"
            style={{ ...inputStyle, background: 'var(--bg-root)', resize: 'none', fontFamily: 'var(--font)' }} />
          <button onClick={addNote} disabled={saving || !note.trim()}
            style={{ marginTop: 8, padding: '10px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving || !note.trim() ? 0.6 : 1 }}>
            {saving ? 'Guardando…' : 'Enviar nota'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Componente: Dashboard principal ──────────────────────────────────────────

function GuardDashboard({ user, token, onLogout }) {
  const [incidents,  setInc]     = useState([])
  const [view,       setView]    = useState('list')   // list | new | detail | ptt | video
  const [selected,   setSelected]= useState(null)
  const [filter,     setFilter]  = useState('active')
  const [toast,      setToast]   = useState(null)
  const [activePtt,  setPtt]     = useState(null)     // datos de llamada entrante
  const [siteId,     setSiteId]  = useState(null)     // sitio del guardia (para PTT)

  // Web Push subscription para alertas urgentes al celular del guardia.
  // state: 'unsupported' | 'pending' | 'granted' | 'denied' | 'enabled'
  const push = usePushSubscription()

  // Determinar sitio del guardia desde el token (primer sitio del tenant)
  useEffect(() => {
    fetch('/api/sites/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(sites => { if (sites.length > 0) setSiteId(sites[0].id) })
      .catch(() => {})
  }, [token])

  const load = useCallback(async () => {
    const res = await fetch('/api/incidents/?limit=30', { headers: { Authorization: `Bearer ${token}` } })
    if (res.ok) setInc((await res.json()).items || [])
  }, [token])

  useEffect(() => { load() }, [load])
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t) }, [load])

  useWebSocket(msg => {
    if (msg.type === 'incident_created') {
      setInc(p => [msg.incident, ...p])
      showToast('🚨 Nuevo incidente: ' + msg.incident.title)
    }
    if (msg.type === 'incident_updated') {
      setInc(p => p.map(i => i.id === msg.incident.id ? { ...i, ...msg.incident } : i))
    }
    // Llamada PTT entrante
    if (msg.type === 'ptt_incoming_call') {
      // Solo atender si es para nuestro sitio, zona, o broadcast
      const isForMe = msg.broadcast || msg.site_id === siteId || msg.zone_id
      if (isForMe) {
        setPtt(msg)
        setView('ptt')
        showToast(`📻 Llamada de ${msg.caller}`)
      }
    }
  })

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000) }

  const displayed = filter === 'active'
    ? incidents.filter(i => ['open', 'acknowledged', 'in_progress'].includes(i.status))
    : incidents
  const openCount = incidents.filter(i => ['open', 'acknowledged', 'in_progress'].includes(i.status)).length

  // Vista PTT
  if (view === 'ptt' && activePtt) {
    return (
      <PttScreen
        call={activePtt}
        token={token}
        onClose={() => { setView('list'); setPtt(null) }}
      />
    )
  }

  // Vista videollamada emergencia
  if (view === 'video') {
    return (
      <EmergencyVideoCall
        token={token}
        siteId={siteId}
        onClose={() => setView('list')}
      />
    )
  }

  if (view === 'new') {
    return (
      <NewIncidentForm
        token={token}
        onCreated={inc => { setInc(p => [inc, ...p]); setView('list'); showToast('✅ Incidente reportado') }}
        onCancel={() => setView('list')}
      />
    )
  }

  if (view === 'detail' && selected) {
    return (
      <IncidentDetail
        inc={selected}
        token={token}
        onBack={() => { setView('list'); setSelected(null) }}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', background: 'var(--bg-root)' }}>
      {/* Header */}
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', padding: '14px 16px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>RetaliQ Guardia</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user?.nombre || user?.email}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {openCount > 0 && (
              <span style={{ background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{openCount}</span>
            )}
            <button onClick={onLogout} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
              Salir
            </button>
          </div>
        </div>
      </div>

      {/* Banner habilitar push */}
      {(push.state === 'granted' || push.state === 'pending') && (
        <div style={{
          padding: '10px 14px', margin: '10px 14px 0',
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>🔔</span>
          <div style={{ flex: 1, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: '#92400e' }}>Notificaciones desactivadas</div>
            <div style={{ color: 'var(--text-muted)' }}>
              Habilitalas para recibir alertas urgentes incluso con la app cerrada.
            </div>
          </div>
          <button onClick={push.enable} style={{
            background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6,
            padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Habilitar</button>
        </div>
      )}
      {push.state === 'enabled' && (
        <div style={{ padding: '6px 14px', fontSize: 10, color: '#10b981', textAlign: 'right' }}>
          🔔 Notificaciones activas · <button onClick={push.sendTest} style={{
            background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', textDecoration: 'underline', fontSize: 10,
          }}>enviar prueba</button>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', padding: '12px 16px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', gap: 0 }}>
        {[['active', 'Activos'], ['all', 'Todos']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            flex: 1, padding: 8, border: 'none', cursor: 'pointer',
            background: filter === k ? 'var(--accent)' : 'transparent',
            color: filter === k ? '#fff' : 'var(--text-muted)',
            fontWeight: filter === k ? 700 : 400, fontSize: 13,
            borderRadius: k === 'active' ? '6px 0 0 6px' : '0 6px 6px 0',
            outline: filter === k ? 'none' : '1px solid var(--border)',
          }}>{label}{k === 'active' && openCount > 0 ? ` (${openCount})` : ''}</button>
        ))}
      </div>

      {/* Lista */}
      <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
        {displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 14 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            {filter === 'active' ? 'Sin incidentes activos' : 'Sin incidentes registrados'}
          </div>
        ) : displayed.map(inc => {
          const sev = SEV[inc.severity] || SEV.medium
          const time = inc.created_at ? new Date(inc.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : ''
          return (
            <div
              key={inc.id}
              onClick={() => { setSelected(inc); setView('detail') }}
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, borderLeft: `4px solid ${sev.color}`, cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: sev.bg, color: sev.color }}>{sev.icon} {sev.label}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, border: '1px solid var(--border)', color: 'var(--text-muted)' }}>{STATUS_LABELS[inc.status]}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{inc.title}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{time}</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Botones de acción */}
      <div style={{ padding: '12px 16px 32px', background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* PTT — Llamar al SOC */}
        <button
          onClick={async () => {
            if (!siteId) { showToast('Sin sitio asignado todavía'); return }
            try {
              const r = await fetch(`/api/ptt/call/site/${siteId}`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: 'Guardia inicia llamada' }),
              })
              if (!r.ok) throw new Error(`call ${r.status}`)
              const d = await r.json()
              // Reusar PttScreen con el token del guardia + room recién creado.
              setPtt({
                caller     : 'SOC RetaliQ',
                room       : d.room,
                livekit_url: d.livekit_url,
                token      : d.token,
                log_id     : d.log_id,
                outgoing   : true,
              })
              setView('ptt')
            } catch (e) {
              showToast('No se pudo conectar con el SOC: ' + e.message)
            }
          }}
          style={{
            width: '100%', padding: '12px', border: 'none', borderRadius: 8,
            background: 'var(--success)', color: '#fff',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          📻 Llamar al SOC
        </button>

        {/* PTT hold-to-talk en grupo seleccionado (default: equipo broadcast) */}
        <PttHoldButton token={token} siteId={siteId} />


        {/* Videollamada de emergencia */}
        <button
          onClick={() => setView('video')}
          style={{
            width: '100%', padding: '12px', border: 'none', borderRadius: 8,
            background: 'var(--warning)', color: '#fff',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          📹 Videollamada emergencia
        </button>

        {/* Reportar incidente */}
        <button
          onClick={() => setView('new')}
          style={{
            ...btnPrimary(true),
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          🚨 Reportar nuevo incidente
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 120, left: 16, right: 16,
          background: '#1E293B', color: '#fff', borderRadius: 10,
          padding: '12px 16px', fontSize: 14, fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 200,
        }}>{toast}</div>
      )}
    </div>
  )
}

// ─── Root ──────────────────────────────────────────────────────────────────────

export default function GuardView() {
  const user    = useAuthStore(s => s.user)
  const token   = useAuthStore(s => s.token)
  const loading = useAuthStore(s => s.loading)
  const refresh = useAuthStore(s => s.refresh)
  const logout  = useAuthStore(s => s.logout)
  const [loggedIn, setLoggedIn] = useState(false)

  useEffect(() => { refresh() }, [])
  useEffect(() => { if (user) setLoggedIn(true) }, [user])

  if (loading) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-root)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Cargando…</span>
    </div>
  )

  if (!user || !loggedIn) return <GuardLogin onLogin={() => setLoggedIn(true)} />

  return (
    <GuardDashboard
      user={user}
      token={token}
      onLogout={() => { logout(); setLoggedIn(false) }}
    />
  )
}
